/**
 * Legal Page Layout Component
 * 
 * Standalone layout for legal pages (Terms of Service, Privacy Policy) 
 * accessed from authentication pages.
 * 
 * Features:
 * - Logo and brand name in header
 * - Breadcrumb navigation back to signup/login (only when not authenticated)
 * - Clean, minimal header design
 * - Full-width content area
 * - Theme support
 * - Accessibility compliance
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ThemeProvider } from '../contexts/ThemeContext';
import { supabase } from '../lib/supabase';

interface LegalPageLayoutProps {
  children: React.ReactNode;
  title: string;
}

export function LegalPageLayout({ children, title: _title }: LegalPageLayoutProps): React.ReactElement {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      setIsLoading(false);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  // Determine where to navigate back to based on referrer or location state
  const getBackPath = (): string => {
    // Check if we have state indicating where we came from
    const state = location.state as { from?: string } | null;
    if (state?.from) {
      return state.from;
    }
    
    // Check referrer
    const referrer = document.referrer;
    if (referrer) {
      if (referrer.includes('/signup')) return '/signup';
      if (referrer.includes('/login')) return '/login';
      if (referrer.includes('/auth')) return '/auth';
    }
    
    // Default to signup
    return '/signup';
  };
  
  const backPath = getBackPath();
  const isSignup = backPath.includes('signup');
  const backText = isSignup ? 'Back to Sign Up' : 'Back to Sign In';
  
  // Only show breadcrumb if not authenticated AND coming from auth pages
  // Check if we have state.from (explicitly from auth page) or referrer indicates auth page
  const hasAuthPageState = !!(location.state as { from?: string } | null)?.from;
  const referrerIsAuthPage = document.referrer && (
    document.referrer.includes('/signup') || 
    document.referrer.includes('/login') || 
    document.referrer.includes('/auth')
  );
  const showBreadcrumb = !isLoading && !isAuthenticated && (hasAuthPageState || referrerIsAuthPage);

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <header className="sticky top-0 z-40 w-full border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:border-gray-700 dark:bg-gray-900/95 supports-[backdrop-filter]:dark:bg-gray-900/60">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              {/* Left side: Breadcrumb (if not authenticated) or Logo */}
              <div className="flex items-center space-x-3">
                {showBreadcrumb ? (
                  <nav aria-label="Breadcrumb">
                    <Link
                      to={backPath}
                      className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors focus-ring rounded-md px-3 py-2"
                    >
                      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                      <span>{backText}</span>
                    </Link>
                  </nav>
                ) : (
                  <>
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-blue-600 flex-shrink-0">
                      <img 
                        src="/logo.png" 
                        alt="Money Mojo" 
                        className="h-6 w-6 object-contain"
                      />
                    </div>
                    <span className="text-xl font-bold text-gray-900 dark:text-gray-100 font-brand tracking-tight">
                      Money Mojo
                    </span>
                  </>
                )}
              </div>

              {/* Right side: Logo (if breadcrumb shown) or empty */}
              {showBreadcrumb && (
                <div className="flex items-center space-x-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-blue-600 flex-shrink-0">
                    <img 
                      src="/logo.png" 
                      alt="Money Mojo" 
                      className="h-6 w-6 object-contain"
                    />
                  </div>
                  <span className="text-xl font-bold text-gray-900 dark:text-gray-100 font-brand tracking-tight">
                    Money Mojo
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content - Add padding-top to prevent H1 from being hidden under sticky header */}
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-8">
          <div className="max-w-4xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </ThemeProvider>
  );
}

export default LegalPageLayout;

